// Start of Selection
// Modified TreeNode.js
import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Plus, ChevronRight, Check, X, ChevronDown, Download, Upload } from 'lucide-react';
import { fetchNodes, createNode, updateNode, deleteNode } from '../../services/assets';
import { useTheme } from '../../context/ThemeContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { AlarmNotification, FaultNotification, ReadingsNotification } from './Notifications';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, NODE_UPDATE_TYPES, NODE_TYPE_PROPERTIES } from '../../utils/constants';

const TreeNode = ({ node, onUpdate, onNodeChange, enabledUpdates, editEnabled, parent = null, siblings = [] }) => {
  const [children, setChildren] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editValue, setEditValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newNodeData, setNewNodeData] = useState({ name: '', type: 'Location', value: '' });
  const [nodeData, setNodeData] = useState({});
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const { theme } = useTheme();
  const { subscribe, unsubscribe } = useWebSocket();
  const { permissions, currentSiteId } = useAuth();

  // Handle WebSocket updates
  const handleUpdate = useCallback((data) => {
    if (enabledUpdates[data.type]) {
      setNodeData((prev) => ({
        ...prev,
        ...data,
      }));
    }
  }, [enabledUpdates]);

  useEffect(() => {
    if (
      node.id &&
      !['Attribute', 'Document'].includes(node.type) &&
      (enabledUpdates.alarms || enabledUpdates.faults || enabledUpdates.readings)
    ) {
      if (NODE_UPDATE_TYPES[node.type]) {
        const updateTypes = Object.keys(NODE_UPDATE_TYPES[node.type]);
        subscribe(node.tag, updateTypes, handleUpdate);

        return () => {
          unsubscribe(node.tag, handleUpdate);
        };
      }
    }
  }, [node.type, node.tag, subscribe, unsubscribe, handleUpdate, enabledUpdates]);

  useEffect(() => {
    if (node.type === 'Attribute') {
      setEditValue(node.value || '');
    }
  }, [node.type, node.value]);

  const loadChildren = useCallback(async () => {
    try {
      const response = await fetchNodes(currentSiteId, node.id, true);
      setChildren(response);
    } catch (error) {
      console.error('Error loading children:', error);
    }
  }, [currentSiteId, node.id]);

  useEffect(() => {
    if (node.id === null && !isExpanded) {
      loadChildren();
    } else if (node.id !== null && isExpanded) {
      loadChildren();
    }
  }, [isExpanded, loadChildren, node.id]);



  const getIcon = (type) => {
    switch(type) {
      case 'Location':
        return <NODE_TYPE_PROPERTIES.Location.icon className={`text-yellow-400`} />;
      case 'Asset':
        return <NODE_TYPE_PROPERTIES.Asset.icon className={`text-indigo-400`} />;
      case 'DataPoint':
        return <NODE_TYPE_PROPERTIES.DataPoint.icon className={`text-blue-400`} />;
      case 'ControlPoint':
        return <NODE_TYPE_PROPERTIES.ControlPoint.icon className={`text-violet-400`} />;
      case 'Document':
        return <NODE_TYPE_PROPERTIES.Document.icon className={`text-gray-400`} />;
      case 'Attribute':
        return <NODE_TYPE_PROPERTIES.Attribute.icon className={`text-gray-400`} />;
      default:
        return <NODE_TYPE_PROPERTIES.Location.icon className={`text-yellow-400`} />;
    }
  };

  
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleEdit = () => {
    if (!editEnabled || !permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS)) return;
    setEditName(node.name);
    setIsEditing(true);
    setError('');
  };

  const handleDelete = async () => {
    if (!editEnabled || !permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS)) return;
    setIsDeleting(true);
    setError('');
  };

  const confirmDelete = async () => {
    try {
      await deleteNode(currentSiteId, node);
      if (onNodeChange) {
        onNodeChange('delete', node.id);
      }
      onUpdate();
      setIsDeleting(false);
    } catch (error) {
      console.error('Error deleting node:', error);
      setError(error.message || 'Failed to delete node');
    }
  };

  const handleSaveEdit = async () => {
    if (!editEnabled || !permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS)) return;
    try {
      const updateData = { name: editName.trim() };
      if (node.type === 'Attribute') {
        updateData.value = editValue.trim();
      }
      const response = await updateNode(currentSiteId, node, siblings, updateData.name, updateData.value);
      if (onNodeChange) {
        onNodeChange('update', node.id, response);
      }
      setIsEditing(false);
      onUpdate();
      setError('');
    } catch (error) {
      console.error('Error updating node:', error);
      setError(error.message || 'Failed to update node');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await updateNode(currentSiteId, node, siblings, parent, null, formData, true);
        if (onNodeChange) {
          onNodeChange('update', node.id, response);
        }
        onUpdate();
      } catch (error) {
        console.error('Error uploading file:', error);
        setError(error.message || 'Failed to upload file');
      }
    }
  };

  const handleFileDownload = async () => {
    if (node.value) {
      try {
        window.open(node.value, '_blank');
      } catch (error) {
        console.error('Error downloading file:', error);
        setError(error.message || 'Failed to download file');
      }
    }
  };

  const handleCreate = async () => {
    if (!editEnabled || !permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS)) return;
    try {
      if (newNodeData.type === 'Document') {
        if (!selectedFile) {
          setError('Please select a file to upload');
          return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('name', newNodeData.name || selectedFile.name.split('.')[0]);
        formData.append('type', 'Document');
        const response = await createNode(currentSiteId, newNodeData.name, 'Document', children, node);
        setChildren(prevChildren => [...prevChildren, response]);
      } else {
        const response = await createNode(currentSiteId, newNodeData.name, newNodeData.type, children, node, newNodeData.value);
        setChildren(prevChildren => [...prevChildren, response]);
      }
      setIsCreating(false);
      setNewNodeData({ name: '', type: 'Location', value: '' });
      setSelectedFile(null);
      setIsExpanded(true);
      setError('');
    } catch (error) {
      console.error('Error creating node:', error);
      setError(error.message || 'Failed to create node');
    }
  };

  const handleChildNodeChange = (action, nodeId, updatedNode) => {
    if (action === 'delete') {
      setChildren(prevChildren => prevChildren.filter(child => child.id !== nodeId));
    } else if (action === 'update') {
      setChildren(prevChildren => 
        prevChildren.map(child => 
          child.id === nodeId ? updatedNode : child
        )
      );
    }
  };


  return (
    <div className="ml-4 w-full">
      <div 
        onClick={() => {
          handleToggle();
        }}
        className={`inline-flex items-center p-2 rounded-lg transition-all duration-200 ${theme === 'dark' ? 'hover:bg-gray-700/60' : 'hover:bg-gray-100/60'} backdrop-blur-sm border border-transparent hover:border-gray-600/20 group cursor-pointer`}
      >
        <div className="flex items-center flex-0">
          {(children.length > 0 || ['Location', 'Asset'].includes(node.type)) ? (
            <span className="mr-2 transition-transform duration-200 hover:scale-110">
              {isExpanded ? <ChevronDown size={16} className="text-indigo-400" /> : <ChevronRight size={16} className="text-indigo-400" />}
            </span>
          ) : (
            <span className="w-4 mr-2" />
          )}
          <div className="transition-transform duration-200 group-hover:scale-110">
          {getIcon(node.type)}
          </div>
          {isEditing ? (
            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className={`ml-2 px-3 py-1 rounded-lg ${theme === 'dark' ? 'bg-gray-800/80 text-white' : 'bg-white/80 text-gray-800'} border border-gray-600/30 focus:ring-2 focus:ring-indigo-500 outline-none`}
                autoFocus
              />
              {node.type === 'Attribute' && (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="Value"
                  className={`px-3 py-1 rounded-lg ${theme === 'dark' ? 'bg-gray-800/80 text-white' : 'bg-white/80 text-gray-800'} border border-gray-600/30 focus:ring-2 focus:ring-indigo-500 outline-none`}
                />
              )}
              <button
                onClick={handleSaveEdit}
                className="p-1.5 rounded-lg hover:bg-green-500/20 text-green-400 transition-colors duration-200"
              >
                <Check size={16} />
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors duration-200"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="ml-2 font-medium flex items-center space-x-2">
              <span>{node.name}</span>
              {node.type === 'Attribute' && <span className="text-gray-500">: {node.value}</span>}
              {node.type === 'Document' && node.value && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileDownload();
                  }}
                  className="p-1.5 rounded-lg hover:bg-blue-500/20 text-blue-400 transition-colors duration-200"
                >
                  <Download size={16} />
                </button>
              )}
            </div>
          )}
          {nodeData && (
            <div className="ml-4 text-sm flex space-x-2">
              {enabledUpdates.alarms && nodeData.alarms !== null && (
                <AlarmNotification count={nodeData.alarms} />
              )}
              {enabledUpdates.faults && nodeData.faults !== null && (
                <FaultNotification count={nodeData.faults} />
              )}
              {enabledUpdates.readings && nodeData.value !== null && (
                <ReadingsNotification value={nodeData.value} quality={nodeData.quality} data_type={nodeData.data_type}/>
              )}
            </div>
          )}
        </div>
        {editEnabled && (
          <div className="ml-16 inline-flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS) && (
              <>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }} 
                  className="p-1.5 rounded-lg hover:bg-indigo-500/20 text-indigo-400 transition-colors duration-200"
                  title="Edit"
                >
                  <Pencil size={16} />
                </button>
                {node.type === 'Document' && (
                  <label className="p-1.5 rounded-lg hover:bg-blue-500/20 text-blue-400 transition-colors duration-200 cursor-pointer">
                    <Upload size={16} />
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      onClick={(e) => e.stopPropagation()}
                      title="Upload Document"
                    />
                  </label>
                )}
              </>
            )}
            {permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS) && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }} 
                className={`p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors duration-200`}
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            )}
            {permissions.includes(PERMISSIONS.ASSETS.EDIT_ASSETS) && ['Location', 'Asset'].includes(node.type) && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setNewNodeData({ 
                    name: '', 
                    type: node.type === 'Location' ? 'Location' : 'DataPoint',
                    value: ''
                  });
                  setIsCreating(true);
                  setError('');
                }} 
                className="p-1.5 rounded-lg hover:bg-emerald-500/20 text-emerald-400 transition-colors duration-200"
                title="Create New"
              >
                <Plus size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {isDeleting && (
        <div className={`mb-4 p-4 rounded-xl z-10 max-w-fit ${theme === 'dark' ? 'bg-gray-800/90' : 'bg-gray-100/90'} backdrop-blur-md border border-indigo-500/20 shadow-lg hover:shadow-xl transition-all duration-300`}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <p className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                Are you sure you want to delete the {node.type} : {node.name}?
              </p>
            </div>
            {error && (
              <p className="text-red-500">{error}</p>
            )}
            <div className="flex gap-2 w-full">
              <button 
                onClick={() => setIsDeleting(false)}
                className={`flex-1 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-red-600/20 hover:bg-red-600/30' : 'bg-red-500/10 hover:bg-red-500/20'} text-red-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center`}
              >
                <X size={18} className="mr-2" />
                <span className="whitespace-nowrap">Cancel</span>
              </button>
              <button 
                onClick={confirmDelete}
                className={`flex-1 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-green-600/20 hover:bg-green-600/30' : 'bg-green-500/10 hover:bg-green-500/20'} text-green-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center`}
              >
                <Check size={18} className="mr-2" />
                <span className="whitespace-nowrap">Confirm</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreating && (
        <div className={`mb-4 p-4 rounded-xl z-10 max-w-fit ${theme === 'dark' ? 'bg-gray-800/90' : 'bg-gray-100/90'} backdrop-blur-md border border-indigo-500/20 shadow-lg hover:shadow-xl transition-all duration-300`}>
          <div className="flex flex-col gap-3">
            {/* Row 1: Type Selection */}
            <div className="flex gap-3">
              <select
                value={newNodeData.type}
                onChange={(e) => setNewNodeData({ ...newNodeData, type: e.target.value })}
                className={`px-3 py-2 rounded-lg w-full ${theme === 'dark' ? 'bg-gray-700/90 text-white' : 'bg-white/90 text-gray-800'} border border-indigo-500/30 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all duration-200 hover:border-indigo-400 cursor-pointer`}
              >
                {node.type === 'Location' ? (
                  <>
                    <option value="Location">Location</option>
                    <option value="Asset">Asset</option>
                    <option value="Document">Document</option>
                    <option value="Attribute">Attribute</option>
                  </>
                ) : node.type === 'Asset' ? (
                  <>
                    <option value="DataPoint">Data Point</option>
                    <option value="ControlPoint">Control Point</option>
                    <option value="Document">Document</option>
                    <option value="Attribute">Attribute</option>
                  </>
                ) : null}
              </select>
            </div>

            {/* Row 2: Name Input */}
            <div className="flex gap-3">
              {newNodeData.type !== 'Document' ? (
                <input
                  type="text"
                  placeholder={`${newNodeData.type.charAt(0).toUpperCase() + newNodeData.type.slice(1)} name`}
                  value={newNodeData.name}
                  onChange={(e) => setNewNodeData({ ...newNodeData, name: e.target.value })}
                  className={`px-3 py-2 rounded-lg w-full ${theme === 'dark' ? 'bg-gray-700/90 text-white placeholder-gray-400' : 'bg-white/90 text-gray-800 placeholder-gray-500'} border border-indigo-500/30 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all duration-200 hover:border-indigo-400`}
                />
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Document name"
                    value={newNodeData.name}
                    onChange={(e) => setNewNodeData({ ...newNodeData, name: e.target.value })}
                    className={`px-3 py-2 rounded-lg w-full ${theme === 'dark' ? 'bg-gray-700/90 text-white placeholder-gray-400' : 'bg-white/90 text-gray-800 placeholder-gray-500'} border border-indigo-500/30 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all duration-200 hover:border-indigo-400`}
                  />
                </>
              )}
            </div>

            {/* Row 3: File Upload for Document or Value for Attribute */}
            {newNodeData.type === 'Document' && (
              <div className="flex gap-3">
                <label className="flex-1 px-4 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center cursor-pointer">
                  <Upload size={18} className="mr-2" />
                  <span>Upload Document</span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const fileName = file.name.split('.')[0];
                        setSelectedFile(file);
                        setNewNodeData({ ...newNodeData, name: fileName });
                      }
                    }}
                  />
                </label>
              </div>
            )}

            {newNodeData.type === 'Attribute' && (
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Value"
                  value={newNodeData.value}
                  onChange={(e) => setNewNodeData({ ...newNodeData, value: e.target.value })}
                  className={`px-3 py-2 rounded-lg w-full ${theme === 'dark' ? 'bg-gray-700/90 text-white placeholder-gray-400' : 'bg-white/90 text-gray-800 placeholder-gray-500'} border border-indigo-500/30 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all duration-200 hover:border-indigo-400`}
                />
              </div>
            )}

            {error && (
              <p className="text-red-500">{error}</p>
            )}
            
            <div className="flex gap-2 w-full">
              <button 
                onClick={() => {
                  setIsCreating(false);
                  setSelectedFile(null);
                }}
                className={`flex-1 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-red-600/20 hover:bg-red-600/30' : 'bg-red-500/10 hover:bg-red-500/20'} text-red-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center`}
              >
                <X size={18} className="mr-2" />
                <span className="whitespace-nowrap">Cancel</span>
              </button>
              <button 
                onClick={handleCreate}
                className={`flex-1 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-green-600/20 hover:bg-green-600/30' : 'bg-green-500/10 hover:bg-green-500/20'} text-green-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center`}
              >
                <Check size={18} className="mr-2" />
                <span className="whitespace-nowrap">Create</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div className="ml-4 w-full">
          {children
            .sort((a, b) => {
              // Define type order
              const typeOrder = {
                'DataPoint': 1,  
                'ControlPoint': 2,
                'Attribute': 3, 
                'Document': 4,
                'Location': 5,
                'Asset': 5 // Group assets with locations
              };
              
              // First sort by type
              if (typeOrder[a.type.toLowerCase()] !== typeOrder[b.type.toLowerCase()]) {
                return typeOrder[a.type.toLowerCase()] - typeOrder[b.type.toLowerCase()];
              }
              
              // Then sort alphabetically within type
              return a.name.localeCompare(b.name);
            })
            .map(childNode => (
              <TreeNode 
                key={childNode.id} 
                node={childNode} 
                onUpdate={onUpdate}
                onNodeChange={handleChildNodeChange}
                enabledUpdates={enabledUpdates}
                editEnabled={editEnabled}
                parent={node}
                siblings={children}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default TreeNode;
